/**
 * SOW template workbench - replaces the old read-only TEMPLATE/DEFINITION cards.
 * Left: document settings + a sections accordion (config). Right rail: a live
 * preview with a sample/placeholder toggle. A view/edit toggle drives full
 * content editing; Save persists the rebuilt contract + preview sample.
 */

import type { DocumentContract } from '@protoss/doc';
import { applyVisualOverrides, type VisualOverrides } from '@protoss/doc';
import { Button, DropdownMenu, TextArea } from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../../../components/ResizableDivider';
import { type PreviewMode, type PreviewSample } from '../../../../components/doc';
import {
  addSection,
  buildSowContract,
  defaultPreviewSample,
  duplicateSection,
  moveSection,
  parseDocumentSettings,
  parseLogicalSections,
  removeSection,
  renameSection,
  reorderSection,
  switchSectionType,
  type LogicalContentType,
  type LogicalSection,
} from '../../lib/sowContract';
import { SowCoverBrandPanel, type SowWorkspaceBrand } from './SowCoverBrandPanel';
import { SowDocumentSettingsPanel } from './SowDocumentSettingsPanel';
import { SowFidelityPanel, type SowFidelityMetadata } from './SowFidelityPanel';
import { SowMockDataEditor } from './SowMockDataEditor';
import { SowPreviewPane } from './SowPreviewPane';
import { SowSectionsAccordion } from './SowSectionsAccordion';

const PREVIEW_WIDTH_KEY = 'sow.previewWidth';
const PREVIEW_MIN = 320;
const PREVIEW_MAX = 760;

export interface SowTemplateForWorkbench {
  id: number;
  uuid: string;
  label: string;
  scope: 'system' | 'workspace' | 'user';
  status?: string;
  isDefault?: boolean;
  contract: unknown;
  metadata?: {
    previewSample?: PreviewSample;
    fidelity?: SowFidelityMetadata;
    /** slot → workspace-asset-uuid map; resolves the contract's {{protoss:asset:slot}} refs. */
    assetSlots?: Record<string, string>;
  } | null;
}

export interface SowTemplateWorkbenchProps {
  template: SowTemplateForWorkbench;
  saving: boolean;
  /** `assetSlots` is sent only when a visual override re-points a slot (e.g. logo swap). */
  onSave: (
    contract: DocumentContract,
    previewSample: PreviewSample,
    assetSlots?: Record<string, string>
  ) => Promise<void>;
  onDuplicate: () => void;
  onPublish: () => void;
  /** Workspace brand assets the Cover & Brand panel can swap a logo to. */
  workspaceBrand: SowWorkspaceBrand;
}

function asContract(value: unknown): DocumentContract | null {
  if (value && typeof value === 'object' && Array.isArray((value as DocumentContract).sections)) {
    return value as DocumentContract;
  }
  return null;
}

function mergeSample(base: PreviewSample, persisted?: PreviewSample | null): PreviewSample {
  if (!persisted) return base;
  return {
    version: '1.0',
    tokens: { ...base.tokens, ...persisted.tokens },
    data: { ...base.data, ...persisted.data },
    narrative: { ...base.narrative, ...persisted.narrative },
  };
}

function readStoredWidth(): number {
  try {
    const raw = window.localStorage?.getItem(PREVIEW_WIDTH_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? Math.min(PREVIEW_MAX, Math.max(PREVIEW_MIN, n)) : 420;
  } catch {
    return 420;
  }
}

export function SowTemplateWorkbench({
  template,
  saving,
  onSave,
  onDuplicate,
  onPublish,
  workspaceBrand,
}: SowTemplateWorkbenchProps) {
  const base = asContract(template.contract);
  const canEdit = template.scope === 'workspace';
  const assetSlots = React.useMemo(
    () => template.metadata?.assetSlots ?? {},
    [template.metadata]
  );
  const workspaceLogoUuid =
    workspaceBrand.documentLogoAssetUuid ?? workspaceBrand.headerLogoAssetUuid ?? null;
  // Always offer publish for a workspace template (even one already active): re-publishing
  // also (re)activates the paired SOW engine profile that drives deal generation, so it is
  // the recovery path when a template was published before that profile existed.
  const canPublish = canEdit;

  // Hooks must run unconditionally; a null contract falls back to raw JSON below.
  const safeBase = base ?? { provider: 'docx', schemaVersion: '0', output: { filename: '' }, document: {}, sections: [] };
  const initialSample = React.useMemo(
    () => mergeSample(defaultPreviewSample(safeBase), template.metadata?.previewSample),
    [template.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Normalized baseline: build the contract from the freshly-parsed base so that
  // buildSowContract's normalization (default margins/orientation) doesn't read as
  // a change. Entering edit with no edits then compares equal → not dirty.
  const baseJson = React.useMemo(
    () =>
      JSON.stringify(
        buildSowContract(safeBase, parseDocumentSettings(safeBase), parseLogicalSections(safeBase))
      ),
    [template.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const initialSampleJson = React.useMemo(() => JSON.stringify(initialSample), [initialSample]);

  const [isEditing, setIsEditing] = React.useState(false);
  const [settings, setSettings] = React.useState(() => parseDocumentSettings(safeBase));
  const [sections, setSections] = React.useState(() => parseLogicalSections(safeBase));
  const [sample, setSample] = React.useState<PreviewSample>(() => initialSample);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [visualOverrides, setVisualOverrides] = React.useState<VisualOverrides>({});
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [previewMode, setPreviewMode] = React.useState<PreviewMode>('sample');
  const [previewWidth, setPreviewWidth] = React.useState(readStoredWidth);
  const [mockOpen, setMockOpen] = React.useState(false);

  const headings = React.useMemo(
    () => sections.filter((s) => s.heading).map((s) => ({ text: s.headingText, level: s.headingLevel ?? 1 })),
    [sections]
  );
  const draftContract = React.useMemo(
    () => buildSowContract(safeBase, settings, sections),
    [safeBase, settings, sections]
  );
  const visualDirty =
    (visualOverrides.assets?.length ?? 0) > 0 ||
    Boolean(visualOverrides.addCoverAssetUuid) ||
    Boolean(visualOverrides.addLogoAssetUuid) ||
    Boolean(visualOverrides.addLogoWorkspace);
  const isDirty = React.useMemo(
    () =>
      JSON.stringify(draftContract) !== baseJson ||
      JSON.stringify(sample) !== initialSampleJson ||
      visualDirty,
    [draftContract, baseJson, sample, initialSampleJson, visualDirty]
  );

  if (!base) {
    return (
      <div className="ptp-sow">
        <p className="ptp-empty-text">
          This template&apos;s contract could not be read. Showing the raw definition.
        </p>
        <TextArea readOnly rows={20} value={JSON.stringify(template.contract, null, 2)} />
      </div>
    );
  }

  // ── Intents ──
  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const rename = (id: string, text: string) => {
    const r = renameSection(sections, sample, id, text);
    setSections(r.sections);
    setSample(r.sample);
  };
  const switchType = (id: string, type: LogicalContentType) => {
    const r = switchSectionType(sections, sample, id, type);
    setSections(r.sections);
    setSample(r.sample);
  };
  const add = (type: LogicalContentType) => {
    const before = new Set(sections.map((s) => s.id));
    const r = addSection(sections, sample, type, selectedId ?? undefined);
    setSections(r.sections);
    setSample(r.sample);
    const created = r.sections.find((s) => !before.has(s.id));
    if (created) {
      setSelectedId(created.id);
      setExpandedIds((prev) => new Set(prev).add(created.id));
    }
  };
  const changeSection = (next: LogicalSection) =>
    setSections((prev) => prev.map((s) => (s.id === next.id ? next : s)));
  const move = (id: string, dir: 'up' | 'down') => setSections(moveSection(sections, id, dir));
  const reorder = (id: string, toIndex: number) => setSections(reorderSection(sections, id, toIndex));
  const duplicate = (id: string) => {
    const r = duplicateSection(sections, sample, id);
    setSections(r.sections);
    setSample(r.sample);
  };
  const remove = (id: string) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this section? This cannot be undone until you cancel editing.')) {
      setSections(removeSection(sections, id));
    }
  };

  const startEdit = () => setIsEditing(true);
  const cancel = () => {
    if (isDirty && !window.confirm('Discard your unsaved changes?')) return; // eslint-disable-line no-alert
    setSettings(parseDocumentSettings(safeBase));
    setSections(parseLogicalSections(safeBase));
    setSample(initialSample);
    setVisualOverrides({});
    setExpandedIds(new Set());
    setIsEditing(false);
  };
  const save = async () => {
    // Apply the cover/logo decisions to the rebuilt contract + slot map (the same shared
    // helper the AI authoring flow uses). assetSlots is only sent when it actually changed
    // (a logo swap re-points a slot); a pure remove edits only the contract.
    if (visualDirty) {
      const result = applyVisualOverrides(draftContract, assetSlots, visualOverrides, workspaceLogoUuid);
      const slotsChanged = JSON.stringify(result.assetSlots) !== JSON.stringify(assetSlots);
      await onSave(result.contract, sample, slotsChanged ? result.assetSlots : undefined);
    } else {
      await onSave(draftContract, sample);
    }
    setVisualOverrides({});
    setIsEditing(false);
  };

  const onResize = (delta: number) =>
    setPreviewWidth((w) => {
      const next = Math.min(PREVIEW_MAX, Math.max(PREVIEW_MIN, w - delta));
      try {
        window.localStorage?.setItem(PREVIEW_WIDTH_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  return (
    <div className="ptp-sow">
      <div className="ptp-sow__header">
        {!canEdit ? (
          <span className="ptp-sow__note">System template - duplicate it to customize.</span>
        ) : (
          <span />
        )}
        <div className="ptp-sow__actions">
          {isEditing ? (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={cancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={save}
                disabled={!isDirty || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {canEdit ? (
                <Button type="button" variant="outline" size="sm" onClick={startEdit}>
                  Edit
                </Button>
              ) : null}
              {canPublish ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onPublish}
                  disabled={saving}
                >
                  Publish
                </Button>
              ) : null}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button type="button" variant={canEdit ? 'ghost' : 'primary'} size="sm">
                    + New
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="dropdown-content">
                  <DropdownMenu.Item className="dropdown-item" onSelect={onDuplicate}>
                    Duplicate this template
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </>
          )}
        </div>
      </div>

      <div className="ptp-sow-split">
        <div className="ptp-sow-main">
          <SowFidelityPanel fidelity={template.metadata?.fidelity} />
          <SowCoverBrandPanel
            contract={draftContract}
            assetSlots={assetSlots}
            workspaceBrand={workspaceBrand}
            isEditing={isEditing}
            overrides={visualOverrides}
            onChange={setVisualOverrides}
          />
          <SowDocumentSettingsPanel settings={settings} isEditing={isEditing} onChange={setSettings} />
          <SowSectionsAccordion
            sections={sections}
            isEditing={isEditing}
            selectedId={selectedId}
            expandedIds={expandedIds}
            sample={sample}
            headings={headings}
            onSelect={setSelectedId}
            onToggle={toggle}
            onRename={rename}
            onSwitchType={switchType}
            onChange={changeSection}
            onSampleChange={setSample}
            onMove={move}
            onDuplicate={duplicate}
            onRemove={remove}
            onReorder={reorder}
            onAdd={add}
          />
          <details className="ptp-sow-raw">
            <summary>Show raw contract</summary>
            <TextArea readOnly rows={16} value={JSON.stringify(draftContract, null, 2)} />
          </details>
        </div>

        <ResizableDivider direction="vertical" onResize={onResize} ariaLabel="Resize preview" />

        <div className="ptp-sow-preview-rail" style={{ width: `${previewWidth}px` }}>
          <SowPreviewPane
            sections={sections}
            sample={sample}
            headings={headings}
            selectedId={selectedId}
            mode={previewMode}
            isEditing={isEditing}
            onModeChange={setPreviewMode}
            onSelect={setSelectedId}
            onEditSample={() => setMockOpen(true)}
          />
        </div>
      </div>

      <SowMockDataEditor
        open={mockOpen}
        sample={sample}
        onClose={() => setMockOpen(false)}
        onChange={setSample}
      />
    </div>
  );
}
